import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { User, ShoppingCart, Star, MessageSquare, Facebook, Instagram } from 'lucide-react';
import PromoCodeSelector from '@/components/sms/promocodes/PromoCodeSelector';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface RestaurantDetails {
  id: string;
  name: string;
  ordering_page_url?: string;
  review_page_url?: string;
  google_review_url?: string;
  facebook_url?: string;
  instagram_url?: string;
}

interface PersonalizationButtonsProps {
  onInsert: (variable: string) => void;
  disabled?: boolean;
  restaurants?: RestaurantDetails[];
  messageContent?: string; // Add prop to track current message content
}

const PersonalizationButtons: React.FC<PersonalizationButtonsProps> = ({
  onInsert,
  disabled = false,
  restaurants = [],
  messageContent = ''
}) => {
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');
  
  // If only one restaurant, auto-select it
  React.useEffect(() => {
    if (restaurants.length === 1) {
      setSelectedRestaurantId(restaurants[0].id);
    }
  }, [restaurants]);

  // Helper function to check if a variable or URL is present in the message
  const isVariableActive = (variable: string) => {
    return messageContent.includes(variable);
  };

  const isUrlActive = (urlField: string) => {
    if (restaurants.length === 0) return false;

    const restaurantId = selectedRestaurantId || restaurants[0].id;
    const restaurant = restaurants.find(r => r.id === restaurantId);

    if (!restaurant) return false;

    const url = restaurant[urlField];
    return url ? messageContent.includes(url) : false;
  };

  /**
   * Check if URL is available for insertion
   */
  const isUrlAvailable = (urlField: string): boolean => {
    if (restaurants.length === 0) {
      return false;
    }

    const restaurantId = selectedRestaurantId || restaurants[0].id;
    const restaurant = restaurants.find(r => r.id === restaurantId);

    if (!restaurant) {
      return false;
    }

    const url = restaurant[urlField];
    return !!url && url.trim() !== '';
  };

  const handleUrlInsert = (urlField: string) => {
    if (restaurants.length === 0) {
      toast({
        title: 'No restaurants available',
        description: 'Please ensure you have access to at least one restaurant.',
        variant: 'destructive'
      });
      return;
    }

    if (restaurants.length > 1 && !selectedRestaurantId) {
      toast({
        title: 'Select a restaurant',
        description: 'Please select a restaurant to use its URLs.',
        variant: 'destructive'
      });
      return;
    }

    const restaurantId = selectedRestaurantId || restaurants[0].id;
    const restaurant = restaurants.find(r => r.id === restaurantId);
    
    if (!restaurant) {
      toast({
        title: 'Restaurant not found',
        description: 'Selected restaurant could not be found.',
        variant: 'destructive'
      });
      return;
    }

    const url = restaurant[urlField];
    
    if (!url) {
      toast({
        title: 'URL not set',
        description: `${urlField.replace(/_/g, ' ')} is not set for this restaurant.`,
        variant: 'destructive'
      });
      return;
    }

    onInsert(url);

    // Show warning for multi-restaurant users
    if (restaurants.length > 1) {
      toast({
        title: 'URL inserted',
        description: 'Note: This URL is specific to the selected restaurant. Automatic restaurant selection for recipients will be added in a future update.',
      });
    }
  };

  return (
    <div className="space-y-4">
      {restaurants.length > 1 && (
        <div>
          <label className="text-sm font-medium">Select Restaurant for URLs</label>
          <Select value={selectedRestaurantId} onValueChange={setSelectedRestaurantId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a restaurant" />
            </SelectTrigger>
            <SelectContent>
              {restaurants.map((restaurant) => (
                <SelectItem key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      <div className="flex flex-wrap gap-2">
        <Button 
          size="sm" 
          variant={isVariableActive('{first name}') ? "default" : "outline"}
          onClick={() => onInsert('{first name}')}
          className={cn(
            "flex items-center",
            isVariableActive('{first name}') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white"
          )}
          disabled={disabled}
          type="button"
        >
          <User className="mr-2 h-4 w-4" />
          First Name
        </Button>
        <Button
          size="sm"
          variant={isUrlActive('ordering_page_url') ? "default" : "outline"}
          onClick={() => handleUrlInsert('ordering_page_url')}
          className={cn(
            "flex items-center",
            isUrlActive('ordering_page_url') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white",
            !isUrlAvailable('ordering_page_url') && "opacity-50 cursor-not-allowed"
          )}
          disabled={disabled || !isUrlAvailable('ordering_page_url')}
          type="button"
        >
          <ShoppingCart className="mr-2 h-4 w-4" />
          Order Link
        </Button>
        <Button
          size="sm"
          variant={isUrlActive('review_page_url') ? "default" : "outline"}
          onClick={() => handleUrlInsert('review_page_url')}
          className={cn(
            "flex items-center",
            isUrlActive('review_page_url') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white",
            !isUrlAvailable('review_page_url') && "opacity-50 cursor-not-allowed"
          )}
          disabled={disabled || !isUrlAvailable('review_page_url')}
          type="button"
        >
          <Star className="mr-2 h-4 w-4" />
          Review Link
        </Button>
        <Button
          size="sm"
          variant={isUrlActive('google_review_url') ? "default" : "outline"}
          onClick={() => handleUrlInsert('google_review_url')}
          className={cn(
            "flex items-center",
            isUrlActive('google_review_url') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white",
            !isUrlAvailable('google_review_url') && "opacity-50 cursor-not-allowed"
          )}
          disabled={disabled || !isUrlAvailable('google_review_url')}
          type="button"
        >
          <MessageSquare className="mr-2 h-4 w-4" />
          Google Review
        </Button>
        <Button
          size="sm"
          variant={isUrlActive('facebook_url') ? "default" : "outline"}
          onClick={() => handleUrlInsert('facebook_url')}
          className={cn(
            "flex items-center",
            isUrlActive('facebook_url') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white",
            !isUrlAvailable('facebook_url') && "opacity-50 cursor-not-allowed"
          )}
          disabled={disabled || !isUrlAvailable('facebook_url')}
          type="button"
        >
          <Facebook className="mr-2 h-4 w-4" />
          Facebook
        </Button>
        <Button
          size="sm"
          variant={isUrlActive('instagram_url') ? "default" : "outline"}
          onClick={() => handleUrlInsert('instagram_url')}
          className={cn(
            "flex items-center",
            isUrlActive('instagram_url') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white",
            !isUrlAvailable('instagram_url') && "opacity-50 cursor-not-allowed"
          )}
          disabled={disabled || !isUrlAvailable('instagram_url')}
          type="button"
        >
          <Instagram className="mr-2 h-4 w-4" />
          Instagram
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {'{first name}'} will be replaced with recipient data. URL buttons insert actual links.
      </p>
      
      <div className="space-y-2 border-t pt-4">
        <label className="text-sm font-medium">Promo Code</label>
        <div className="flex gap-2">
          <PromoCodeSelector
            restaurantId={selectedRestaurantId || (restaurants.length === 1 ? restaurants[0].id : undefined)}
            onSelectCode={(code, codeId) => {
              onInsert(code);
            }}
            disabled={disabled}
            messageContent={messageContent}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Insert a promo code to track template effectiveness and enable ROI calculation
        </p>
      </div>
    </div>
  );
};

export default PersonalizationButtons;